import {
  createSlice,
  createEntityAdapter,
  createSelector,
} from "@reduxjs/toolkit";

const generalAdapter = createEntityAdapter({
  selectId: (general) => general.id,
  sortComparer: (a, b) => a.name.localeCompare(b.name),
});
const initialState = generalAdapter.getInitialState({
  showRegisterModal: false,
  registerModalType: "signupBtn",
  // New: Store email/password from AuthModal for RegisterModal
  registerEmail: "",
  registerPassword: "",
  googleUserData: null, // Store Google user data for RegisterModal
  createProjectAfterLogin: false,
  redirectUrl: "",
  isLoggedIn: false,
  resultEditor: "",
  isHideNavbar: false,
  authLoading: false,
  signMessageModalOpen: false,
  loginLoading: false,
  showAccountPopup: false,
  showNetworkPopup: false,
  showExpireSessionPopup: false,
  disconnectRedirectUrl: null,
  prevLoginProps: undefined,
  isScrolledToTop: false,
  // New auth modal states
  showAuthModal: false,
  authModalRedirect: null,
  showWalletRequiredModal: false,
  walletRequiredAction: null,
  walletRequiredRedirect: null, // URL to redirect after wallet linking
  walletJustLinked: false, // Flag to indicate wallet was just linked
});
export const generalSlice = createSlice({
  name: "general",
  initialState,
  reducers: {
    setShowRegisterModal: (state, action) => {
      // Support both simple boolean and object with email/password/googleUserData
      if (typeof action.payload === "object") {
        state.showRegisterModal = action.payload.show;
        state.registerEmail = action.payload.email || "";
        state.registerPassword = action.payload.password || "";
        state.googleUserData = action.payload.googleUserData || null;
      } else {
        state.showRegisterModal = action.payload;
        if (!action.payload) {
          // Clear email/password/googleUserData when closing modal
          state.registerEmail = "";
          state.registerPassword = "";
          state.googleUserData = null;
        }
      }
    },
    setRegisterModalType: (state, action) => {
      state.registerModalType = action.payload;
    },
    setCreateProjectAfterLogin: (state, action) => {
      state.createProjectAfterLogin = action.payload;
    },
    setRedirectUrl: (state, action) => {
      state.redirectUrl = action.payload;
    },
    setIsLoggedIn: (state, action) => {
      state.isLoggedIn = action.payload;
    },
    setResultEditor: (state, action) => {
      state.resultEditor = action.payload;
    },
    setHideNavbar: (state, action) => {
      state.isHideNavbar = action.payload;
    },
    setAuthLoading: (state, action) => {
      state.authLoading = action.payload;
    },
    setSignMessageModalOpen: (state, action) => {
      state.signMessageModalOpen = action.payload;
    },
    setLoginLoading: (state, action) => {
      state.loginLoading = action.payload;
    },
    setShowAccountPopup: (state, action) => {
      state.showAccountPopup = action.payload;
    },
    setShowNetworkPopup: (state, action) => {
      state.showNetworkPopup = action.payload;
    },
    setShowExpireSessionPopup: (state, action) => {
      state.showExpireSessionPopup = action.payload;
    },
    setDisconnectRedirectUrl: (state, action) => {
      state.showExpireSessionPopup = action.payload;
    },
    setPrevLoginProps: (state, action) => {
      state.prevLoginProps = action.payload;
    },
    setScrolledToTop: (state, action) => {
      state.isScrolledToTop = action.payload;
    },
    setShowFullscreenImage: (state, action) => {
      state.showFullscreenImage = action.payload;
    },
    // New auth modal actions
    setShowAuthModal: (state, action) => {
      if (typeof action.payload === "object") {
        state.showAuthModal = action.payload.show;
        state.authModalRedirect = action.payload.redirect || null;
      } else {
        state.showAuthModal = action.payload;
        state.authModalRedirect = null;
      }
    },
    setShowWalletRequiredModal: (state, action) => {
      if (typeof action.payload === "object") {
        state.showWalletRequiredModal = action.payload.show;
        state.walletRequiredAction = action.payload.action || null;
        state.walletRequiredRedirect = action.payload.redirect || null;
      } else {
        state.showWalletRequiredModal = action.payload;
        state.walletRequiredAction = null;
        state.walletRequiredRedirect = null;
      }
    },
    setWalletJustLinked: (state, action) => {
      state.walletJustLinked = action.payload;
    },
  },
});

export const {
  setShowRegisterModal,
  setRegisterModalType,
  setCreateProjectAfterLogin,
  setRedirectUrl,
  setIsLoggedIn,
  setResultEditor,
  setHideNavbar,
  setAuthLoading,
  setSignMessageModalOpen,
  setLoginLoading,
  setShowAccountPopup,
  setShowNetworkPopup,
  setShowExpireSessionPopup,
  setDisconnectRedirectUrl,
  setPrevLoginProps,
  setScrolledToTop,
  setShowFullscreenImage,
  // New auth modal exports
  setShowAuthModal,
  setShowWalletRequiredModal,
  setWalletJustLinked,
} = generalSlice.actions;

// Selectors
export const selectShowRegisterModal = (state) =>
  state.general.showRegisterModal;
export const selectRegisterModalType = (state) =>
  state.general.registerModalType;
export const selectRegisterEmail = (state) =>
  state.general.registerEmail;
export const selectRegisterPassword = (state) =>
  state.general.registerPassword;
export const selectCreateProjectAfterLogin = (state) =>
  state.general.createProjectAfterLogin;
export const selectRedirectUrl = (state) => state.general.redirectUrl;
export const selectIsLoggedIn = (state) => state.general.isLoggedIn;
export const selectResultEditor = (state) => state.general.resultEditor;
export const selectIsHideNavbar = (state) => state.general.isHideNavbar;
export const selectAuthLoading = (state) => state.general.authLoading;
export const selectSignMessageModalOpen = (state) =>
  state.general.signMessageModalOpen;
export const selectLoginLoading = (state) => state.general.loginLoading;
export const selectShowAccountPopup = (state) => state.general.showAccountPopup;
export const selectShowNetworkPopup = (state) => state.general.showNetworkPopup;
export const selectShowExpireSessionPopup = (state) =>
  state.general.showExpireSessionPopup;
export const selectDisconnectRedirectUrl = (state) =>
  state.general.disconnectRedirectUrl;
export const selectPrevLoginProps = (state) => state.general.prevLoginProps;
export const selectIsScrolledToTop = (state) => state.general.isScrolledToTop;
export const selectShowFullscreenImage = (state) =>
  state.general.showFullscreenImage;
// New auth selectors
export const selectShowAuthModal = (state) => state.general.showAuthModal;
export const selectAuthModalRedirect = (state) => state.general.authModalRedirect;
export const selectShowWalletRequiredModal = (state) => state.general.showWalletRequiredModal;
export const selectWalletRequiredAction = (state) => state.general.walletRequiredAction;
export const selectWalletRequiredRedirect = (state) => state.general.walletRequiredRedirect;
export const selectWalletJustLinked = (state) => state.general.walletJustLinked;

export const selectGeneral = createSelector(
  [
    selectShowRegisterModal,
    selectRegisterModalType,
    selectRegisterEmail,
    selectRegisterPassword,
    selectCreateProjectAfterLogin,
    selectRedirectUrl,
    selectIsLoggedIn,
    selectResultEditor,
    selectIsHideNavbar,
    selectAuthLoading,
    selectSignMessageModalOpen,
    selectLoginLoading,
    selectShowAccountPopup,
    selectShowNetworkPopup,
    selectShowExpireSessionPopup,
    selectDisconnectRedirectUrl,
    selectPrevLoginProps,
    selectIsScrolledToTop,
    selectShowFullscreenImage,
    selectShowAuthModal,
    selectAuthModalRedirect,
    selectShowWalletRequiredModal,
    selectWalletRequiredAction,
    selectWalletRequiredRedirect,
    selectWalletJustLinked,
  ],
  (
    showRegisterModal,
    registerModalType,
    registerEmail,
    registerPassword,
    createProjectAfterLogin,
    redirectUrl,
    isLoggedIn,
    resultEditor,
    isHideNavbar,
    authLoading,
    signMessageModalOpen,
    loginLoading,
    showAccountPopup,
    showNetworkPopup,
    showExpireSessionPopup,
    disconnectRedirectUrl,
    prevLoginProps,
    isScrolledToTop,
    showFullscreenImage,
    showAuthModal,
    authModalRedirect,
    showWalletRequiredModal,
    walletRequiredAction,
    walletRequiredRedirect,
    walletJustLinked
  ) => ({
    showRegisterModal,
    registerModalType,
    registerEmail,
    registerPassword,
    createProjectAfterLogin,
    redirectUrl,
    isLoggedIn,
    resultEditor,
    isHideNavbar,
    authLoading,
    signMessageModalOpen,
    loginLoading,
    showAccountPopup,
    showNetworkPopup,
    showExpireSessionPopup,
    disconnectRedirectUrl,
    prevLoginProps,
    isScrolledToTop,
    showFullscreenImage,
    showAuthModal,
    authModalRedirect,
    showWalletRequiredModal,
    walletRequiredAction,
    walletRequiredRedirect,
    walletJustLinked,
  })
);
export default generalSlice.reducer;
